// Centralized labels system for Dendromap
// This file contains all text labels used throughout the application

export interface Labels {
  common: {
    appName: string;
    loading: string;
    error: string;
    success: string;
    cancel: string;
    save: string;
    delete: string;
    edit: string;
    view: string;
    print: string;
    back: string;
    next: string;
    previous: string;
    close: string;
    confirm: string;
    search: string;
    filter: string;
    sort: string;
    export: string;
    import: string;
    refresh: string;
  };

  navigation: {
    home: string;
    solutions: string;
    services: string;
    pricing: string;
    about: string;
    contact: string;
    dashboard: string;
    inventory: string;
    trees: string;
    reports: string;
    settings: string;
    profile: string;
    logout: string;
  };

  auth: {
    login: string;
    register: string;
    logout: string;
    email: string;
    password: string;
    confirmPassword: string;
    forgotPassword: string;
    resetPassword: string;
    rememberMe: string;
    signIn: string;
    signUp: string;
    signOut: string;
    createAccount: string;
    alreadyHaveAccount: string;
    dontHaveAccount: string;
  };

  home: {
    hero: {
      title: string;
      subtitle: string;
      description: string;
      ctaPrimary: string;
      ctaSecondary: string;
      stats: {
        projects: string;
        satisfaction: string;
        support: string;
      };
      form: {
        title: string;
        fullName: string;
        fullNamePlaceholder: string;
        email: string;
        emailPlaceholder: string;
        company: string;
        companyPlaceholder: string;
        submit: string;
      };
    };
    header: {
      brand: string;
      menu: {
        solutions: string;
        services: string;
        pricing: string;
        about: string;
        contact: string;
      };
      actions: {
        login: string;
        start: string;
      };
    };
    footer: {
      copyright: string;
      rights: string;
      links: {
        privacy: string;
        terms: string;
        support: string;
      };
    };
  };

  inventory: {
    title: string;
    management: string;
    subtitle: string;
    create: string;
    edit: string;
    view: string;
    delete: string;
    duplicate: string;
    print: string;
    intervention: string;
    details: string;
    map: string;
    list: string;
    search: string;
    filter: string;
    sort: string;
    export: string;
    import: string;
    newInventory: string;
    mapView: string;
    searchPlaceholder: string;
    retry: string;
    notFound: string;
    loading: string;
    error: string;
    success: string;
    deleteConfirm: string;
    deleteDescription: string;
    deleteSuccess: string;
    deleteError: string;
    status: {
      draft: string;
      inProgress: string;
      completed: string;
      archived: string;
      finished: string;
      notFinished: string;
      all: string;
    };
    types: {
      tree: string;
      epaysage: string;
      alignment: string;
      all: string;
    };
    filters: {
      finished: string;
      notFinished: string;
      all: string;
    };
    columns: {
      select: string;
      type: string;
      genre: string;
      species: string;
      cultivar: string;
      subjectCount: string;
      height: string;
      diameter: string;
      address: string;
      city: string;
      country: string;
      siteCode: string;
      identification: string;
      initialDate: string;
      lastIntervention: string;
      surveyor: string;
    };
    detailLabels: {
      generalInfo: string;
      treeDetails: string;
      epaysageDetails: string;
      user: string;
      creationDate: string;
      lastUpdate: string;
      vernacularName: string;
      essenceCount: string;
      inventoryNumber: string;
      modify: string;
    };
  };

  tree: {
    identification: string;
    location: string;
    characteristics: string;
    health: string;
    maintenance: string;
    value: string;
    photos: string;
    documents: string;
    history: string;
    workRecommendations: string;
    intervention: {
      title: string;
      number: string;
      startDate: string;
      endDate: string;
      client: string;
      address: string;
      gpsLocation: string;
      species: string;
      genus: string;
      cultivar: string;
      description: string;
      work: string;
      comments: string;
      remarks: string;
      interveners: string;
      signature: string;
      mapLocation: string;
      equipment: string;
      model: string;
      type: string;
    };
  };

  sidebar: {
    brand: string;
    tagline: string;
    expandSidebar: string;
    collapseSidebar: string;
    navigation: {
      dashboard: string;
      mapView: string;
      treeInventory: string;
      inventaireArbre: string;
      inventaireAlignement: string;
      inventaireEspaceBoise: string;
      workManagement: string;
      newPlantation: string;
      validationPending: string;
      aiAssistant: string;
      smartMap: string;
    };
    workManagement: {
      alerts: string;
      work: string;
      visite: string;
    };
    newPlantation: {
      createArbre: string;
      createAlignement: string;
      createEspaceBoise: string;
    };
    configuration: {
      title: string;
      promoCode: string;
      printTemplates: string;
      contactManagement: string;
      aiSettings: string;
      geoLayers: string;
    };
    administration: {
      title: string;
      rightsManagement: string;
      savedLists: string;
      userStats: string;
    };
    rightsManagement: {
      group: string;
      profile: string;
      user: string;
      quickCreate: string;
    };
    savedLists: {
      essences: string;
      mushrooms: string;
    };
    superadmin: {
      title: string;
      openSpace: string;
      backToAdmin: string;
      clients: string;
      metrics: string;
      features: string;
      aiUsage: string;
      announcements: string;
      designSystem: string;
      mobileReleases: string;
      menus: string;
      pricing: string;
      promoCode: string;
      aiSettings: string;
      userStats: string;
      auditLogs: string;
      debug: string;
      paymentStats: string;
      paymentLifecycle: string;
      sendTestSms: string;
      subscriptionHelper: string;
    };
  };
}

// French labels (default)
export const labelsFr: Labels = {
  common: {
    appName: "Dendromap",
    loading: "Chargement...",
    error: "Erreur",
    success: "Succès",
    cancel: "Annuler",
    save: "Enregistrer",
    delete: "Supprimer",
    edit: "Modifier",
    view: "Voir",
    print: "Imprimer",
    back: "Retour",
    next: "Suivant",
    previous: "Précédent",
    close: "Fermer",
    confirm: "Confirmer",
    search: "Rechercher",
    filter: "Filtrer",
    sort: "Trier",
    export: "Exporter",
    import: "Importer",
    refresh: "Actualiser",
  },

  navigation: {
    home: "Accueil",
    solutions: "Solutions",
    services: "Services",
    pricing: "Tarifs",
    about: "À propos",
    contact: "Contact",
    dashboard: "Tableau de bord",
    inventory: "Inventaires",
    trees: "Arbres",
    reports: "Rapports",
    settings: "Paramètres",
    profile: "Profil",
    logout: "Déconnexion",
  },

  auth: {
    login: "Se connecter",
    register: "S'inscrire",
    logout: "Se déconnecter",
    email: "Email",
    password: "Mot de passe",
    confirmPassword: "Confirmer le mot de passe",
    forgotPassword: "Mot de passe oublié",
    resetPassword: "Réinitialiser le mot de passe",
    rememberMe: "Se souvenir de moi",
    signIn: "Connexion",
    signUp: "Inscription",
    signOut: "Déconnexion",
    createAccount: "Créer un compte",
    alreadyHaveAccount: "Vous avez déjà un compte ?",
    dontHaveAccount: "Vous n'avez pas de compte ?",
  },

  home: {
    hero: {
      title: "Révolutionnez vos",
      subtitle: "inventaires d'arbres",
      description: "Gérez efficacement vos espaces verts avec notre plateforme innovante de cartographie et d'inventaire des arbres.",
      ctaPrimary: "Démarrer gratuitement",
      ctaSecondary: "Voir la démo",
      stats: {
        projects: "Projets réalisés",
        satisfaction: "Satisfaction client",
        support: "Support technique",
      },
      form: {
        title: "Demande d'information",
        fullName: "Nom complet",
        fullNamePlaceholder: "Votre nom",
        email: "Email",
        emailPlaceholder: "votre@email.com",
        company: "Entreprise",
        companyPlaceholder: "Nom de l'entreprise",
        submit: "Envoyer la demande",
      },
    },
    header: {
      brand: "Dendromap",
      menu: {
        solutions: "Solutions",
        services: "Services",
        pricing: "Tarifs",
        about: "À propos",
        contact: "Contact",
      },
      actions: {
        login: "Se connecter",
        start: "Commencer",
      },
    },
    footer: {
      copyright: "© 2024 Dendromap",
      rights: "Tous droits réservés",
      links: {
        privacy: "Confidentialité",
        terms: "Conditions d'utilisation",
        support: "Support",
      },
    },
  },

  inventory: {
    title: "Inventaires",
    management: "Gestion des Inventaires",
    subtitle: "Consultez, filtrez et gérez vos inventaires",
    create: "Créer",
    edit: "Modifier",
    view: "Voir",
    delete: "Supprimer",
    duplicate: "Dupliquer",
    print: "Imprimer",
    intervention: "Fiche Intervention",
    details: "Détails",
    map: "Carte",
    list: "Liste",
    search: "Rechercher",
    filter: "Filtrer",
    sort: "Trier",
    export: "Exporter",
    import: "Importer",
    newInventory: "Nouvel Inventaire",
    mapView: "Vue Carte",
    searchPlaceholder: "Rechercher par type, adresse, ville...",
    retry: "Réessayer",
    notFound: "L'inventaire n'a pas été trouvé",
    loading: "Chargement...",
    error: "Erreur",
    success: "Succès",
    deleteConfirm: "Êtes-vous sûr de vouloir supprimer ?",
    deleteDescription: "Vous êtes sur le point de supprimer {count} inventaire(s). Cette action est irréversible.",
    deleteSuccess: "Inventaires supprimés avec succès",
    deleteError: "Erreur lors de la suppression des inventaires",
    status: {
      draft: "Brouillon",
      inProgress: "En cours",
      completed: "Terminé",
      archived: "Archivé",
      finished: "Terminé",
      notFinished: "En cours",
      all: "Tous",
    },
    types: {
      tree: "Arbre",
      epaysage: "Espace boisé",
      alignment: "Alignement",
      all: "Tous les types",
    },
    filters: {
      finished: "Inventaires terminés",
      notFinished: "Inventaires en cours",
      all: "Tous les inventaires",
    },
    columns: {
      select: "Sélectionner",
      type: "Type",
      genre: "Genre",
      species: "Espèce",
      cultivar: "Cultivar",
      subjectCount: "Nb de sujets",
      height: "Hauteur",
      diameter: "Diamètre",
      address: "Adresse",
      city: "Ville",
      country: "Pays",
      siteCode: "Code Site",
      identification: "Identification",
      initialDate: "Date inventaire initial",
      lastIntervention: "Dernière intervention",
      surveyor: "Recenseur",
    },
    detailLabels: {
      generalInfo: "Informations générales",
      treeDetails: "Détails de l'arbre",
      epaysageDetails: "Détails de l'espace boisé",
      user: "Utilisateur",
      creationDate: "Date de création",
      lastUpdate: "Dernière mise à jour",
      vernacularName: "Nom vernaculaire",
      essenceCount: "Nombre d'essences",
      inventoryNumber: "Inventaire",
      modify: "Modifier",
    },
  },

  tree: {
    identification: "Identification",
    location: "Localisation",
    characteristics: "Caractéristiques",
    health: "Santé",
    maintenance: "Maintenance",
    value: "Valeur",
    photos: "Photos",
    documents: "Documents",
    history: "Historique",
    workRecommendations: "Préconisations de travaux",
    intervention: {
      title: "FICHE D'INTERVENTION",
      number: "Numéro d'intervention",
      startDate: "Date et heure de début",
      endDate: "Date et heure de fin",
      client: "Nom du client",
      address: "Adresse",
      gpsLocation: "Localisation GPS du point arbre",
      species: "Espèce",
      genus: "Genre",
      cultivar: "Cultivar",
      description: "Description",
      work: "Travaux",
      comments: "Commentaires",
      remarks: "Environnements et observations",
      interveners: "Liste des intervenants",
      signature: "Signature du client",
      mapLocation: "Localisation sur carte",
      equipment: "Equipement",
      model: "Modèle",
      type: "Type",
    },
  },

  sidebar: {
    brand: "DENDROMAP",
    tagline: "Gestion des arbres",
    expandSidebar: "Développer la barre latérale",
    collapseSidebar: "Réduire la barre latérale",
    navigation: {
      dashboard: "Tableau de bord",
      mapView: "Carte des inventaires",
      treeInventory: "Liste des inventaires",
      inventaireArbre: "Arbre",
      inventaireAlignement: "Alignement",
      inventaireEspaceBoise: "Espace boisé",
      workManagement: "Gestion des travaux",
      newPlantation: "Nouvelle implantation",
      validationPending: "Brouillon",
      aiAssistant: "Assistant IA",
      smartMap: "Carte intelligente",
      library: "Bibliothèque",
    },
    library: {
      essences: "Liste des essences",
      mushrooms: "Liste des champignons",
    },
    workManagement: {
      alerts: "Alertes",
      work: "Travaux",
      visite: "Visites",
    },
    newPlantation: {
      createArbre: "Créer un arbre",
      createAlignement: "Créer un alignement",
      createEspaceBoise: "Créer un espace boisé",
    },
    configuration: {
      title: "CONFIGURATION",
      promoCode: "Codes promo",
      printTemplates: "Modèles de fiche",
      contactManagement: "Gestion des contacts",
      aiSettings: "Paramètres IA",
      geoLayers: "Couches géographiques",
    },
    administration: {
      title: "ADMINISTRATION",
      rightsManagement: "Gestion des droits",
      savedLists: "Listes enregistrées",
      userStats: "Stats utilisateurs",
    },
    rightsManagement: {
      group: "Groupe",
      profile: "Profil",
      user: "Utilisateur",
      quickCreate: "Création rapide",
    },
    savedLists: {
      essences: "Essences",
      mushrooms: "Champignons",
    },
    superadmin: {
      title: "Superadmin",
      openSpace: "Espace superadmin",
      backToAdmin: "Retour à l'application",
      clients: "Clients",
      metrics: "Indicateurs",
      features: "Fonctionnalités",
      aiUsage: "Consommation IA",
      announcements: "Annonces & maintenance",
      designSystem: "Design system",
      mobileReleases: "App mobile",
      menus: "Menus par rôle",
      pricing: "Tarifs & abonnements",
      promoCode: "Codes promo",
      aiSettings: "Paramètres IA",
      userStats: "Statistiques utilisateurs",
      auditLogs: "Journaux d'audit",
      debug: "Outils de debug",
      paymentStats: "Statistiques paiements",
      paymentLifecycle: "Cycle de paiement",
      sendTestSms: "SMS de test",
      subscriptionHelper: "Aide abonnements",
    },
  },
};

// Export default labels (French)
export const labels = labelsFr;

// Utility function to get nested label value
export function getLabel(path: string, fallback?: string): string {
  const keys = path.split('.');
  let value: any = labels;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return fallback || path;
    }
  }

  return typeof value === 'string' ? value : fallback || path;
}

// Hook for using labels in React components
export function useLabels() {
  return {
    labels,
    getLabel,
  };
}
